import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription, lastValueFrom } from 'rxjs';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { ChannelGroup } from 'ops/common/models/channel-group';
import { CustomGroupsService } from '@service/custom-groups.service';
import { ChannelGroupService } from '@service/channel-groups.service';
import { CustomGroup } from 'ops/common/models/custom-group';
import {
    DashboardPanelParameters,
    createAlarmPanelParameters,
    createTrendGraphPanelParameters,
    createChannelGroupPanelParameters,
    createCustomGroupPanelParameters,
    createMimicPanelParameters,
} from 'ops/common/models/dashboard-panel-parameters';
import { DashboardPanelType } from 'ops/common/models/dashboard-panel-type';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import { TranslateService } from '@ngx-translate/core';
import { ResponsiveService } from '@service/responsive.service';
import { MimicService } from '@service/mimic.service';

@Component({
    selector: 'app-dashboard-panel-add-form',
    templateUrl: './dashboard-panel-add-form.component.html',
    styleUrls: ['./dashboard-panel-add-form.component.scss'],
    standalone: false
})
export class DashboardPanelAddFormComponent implements OnInit, OnDestroy {
    @Output() onPanelSelected = new EventEmitter<DashboardPanelParameters>();
    @Output() onPreviewClick = new EventEmitter();
    @Output() onClose = new EventEmitter();
    @Output() onSave = new EventEmitter();

    DashboardPanelType = DashboardPanelType;

    searchInput: string = '';

    typeStrings: { [key: string]: string } = {};
    typeFilters: DashboardPanelType[];
    selectedTypeFilters: { [key: string]: boolean } = {};

    trendGraphs: TrendGraphView[] = [];
    channelGroups: ChannelGroup[] = [];
    customGroups: CustomGroup[] = [];
    mimics: any[] = [];
    alarmPages = ['dashboard.forms.add-panel.labels.alarm-monitoring'];

    filteredTrendGraphs: TrendGraphView[] = [];
    filteredChannelGroups: ChannelGroup[] = [];
    filteredCustomGroups: CustomGroup[] = [];
    filteredMimics: any[] = [];
    filteredAlarmPages: string[] = [];

    private hmiSubscription: Subscription;
    selectedPanel: DashboardPanelParameters = {
        type: null,
        params: {} as any,
    };

    constructor(
        private graphService: TrendGraphViewsService,
        private channelGroupService: ChannelGroupService,
        private customGroupService: CustomGroupsService,
        private translateService: TranslateService,
        public responsive: ResponsiveService,
        private mimicService: MimicService,
    ) {
        this.getTypeFilterLabel = this.getTypeFilterLabel.bind(this);

        this.typeFilters = [
            DashboardPanelType.MIMICS,
            DashboardPanelType.TREND_GRAPH,
            DashboardPanelType.CHANNEL_GROUP,
            DashboardPanelType.ALARMS,
        ];

        this.typeStrings[DashboardPanelType.TREND_GRAPH] = 'Trend Graphs';
        this.typeStrings[DashboardPanelType.CHANNEL_GROUP] = 'Channel Groups';
        this.typeStrings[DashboardPanelType.ALARMS] = 'Alarms';
        this.typeStrings[DashboardPanelType.MIMICS] = 'MIMIC';

        this.selectAllTypeFilters();
    }

    async ngOnInit(): Promise<void> {
        this.mimics = await this.mimicService.getAll();

        let [graphs, groups, customGroups] = await Promise.all([
            lastValueFrom(this.graphService.getAll()),
            lastValueFrom(this.channelGroupService.getGroups()),
            this.customGroupService.getGroups(),
        ]);

        this.translateAlarmPages();

        this.trendGraphs = graphs;
        this.channelGroups = groups;
        this.customGroups = customGroups;
        this.filteredChannelGroups = this.channelGroups;
        this.filteredTrendGraphs = this.trendGraphs;
        this.filteredCustomGroups = this.customGroups;
        this.filteredAlarmPages = this.alarmPages;
        this.filteredMimics = this.mimics;
    }

    ngOnDestroy(): void {
        this.hmiSubscription?.unsubscribe();
    }

    async loadHmi() {}

    translateAlarmPages() {
        this.alarmPages.forEach((pageName, i) => {
            this.translateService.get(pageName).subscribe((txt: string) => {
                this.alarmPages[i] = txt;
            });
        });
    }

    handleFiltersUpdated(filters: DashboardPanelType[]) {
        if (filters.length === 0) {
            this.selectAllTypeFilters();
        } else {
            const selectedFilterSet = new Set(filters);
            for (let i = 0; i < this.typeFilters.length; i++) {
                this.selectedTypeFilters[this.typeFilters[i]] = selectedFilterSet.has(
                    this.typeFilters[i],
                );
            }
        }
    }

    handleSearchInputUpdate() {
        if (this.searchInput.length === 0) {
            this.filteredChannelGroups = this.channelGroups;
            this.filteredTrendGraphs = this.trendGraphs;
            this.filteredCustomGroups = this.customGroups;
            this.filteredMimics = this.mimics;
            this.filteredAlarmPages = this.alarmPages;
        } else {
            this.filteredChannelGroups = this.channelGroups.filter((group) => {
                return group.displayName.toLowerCase().includes(this.searchInput.toLowerCase());
            });
            this.filteredTrendGraphs = this.trendGraphs.filter((graph) => {
                return graph.name.toLowerCase().includes(this.searchInput.toLowerCase());
            });
            this.filteredCustomGroups = this.customGroups.filter((group) => {
                return group.displayName.toLowerCase().includes(this.searchInput.toLowerCase());
            });
            this.filteredMimics = this.mimics.filter((mimic) => {
                return mimic.name.toLowerCase().includes(this.searchInput.toLowerCase());
            });
            this.filteredAlarmPages = this.alarmPages.filter((pageName) => {
                return pageName.toLowerCase().includes(this.searchInput.toLowerCase());
            });
        }
    }

    selectAllTypeFilters() {
        for (let i = 0; i < this.typeFilters.length; i++) {
            this.selectedTypeFilters[this.typeFilters[i]] = true;
        }
    }

    isTypeVisible(type: DashboardPanelType) {
        return this.selectedTypeFilters[type];
    }

    getTypeFilterLabel(type: DashboardPanelType) {
        return this.typeStrings[type];
    }

    handleAlarmSelected() {
        this.selectedPanel = createAlarmPanelParameters();
        this.emitPanelSelected(createAlarmPanelParameters());
    }

    handleTrendGraphSelected(graph: TrendGraphView) {
        this.selectedPanel = createTrendGraphPanelParameters(graph);
        this.emitPanelSelected(createTrendGraphPanelParameters(graph));
    }

    handleChannelGroupSelected(group: ChannelGroup) {
        this.selectedPanel = createChannelGroupPanelParameters(group);
        this.emitPanelSelected(createChannelGroupPanelParameters(group));
    }

    handleCustomGroupSelected(group: CustomGroup) {
        this.selectedPanel = createCustomGroupPanelParameters(group);
        this.emitPanelSelected(createCustomGroupPanelParameters(group));
    }

    handleMimicSelected(mimic: any) {
        this.selectedPanel = createMimicPanelParameters(mimic);
        this.emitPanelSelected(createMimicPanelParameters(mimic));
    }

    emitPanelSelected(param: DashboardPanelParameters) {
        this.onPanelSelected.emit(param);
    }

    emitClose() {
        this.onClose.emit();
    }

    emitSave() {
        this.onSave.emit();
    }

    isAlarmSelected() {
        return this.selectedPanel.type === DashboardPanelType.ALARMS;
    }

    isGraphSelected(graph: TrendGraphView) {
        return (
            this.selectedPanel.type === DashboardPanelType.TREND_GRAPH &&
            this.selectedPanel.params.graphId === graph.id
        );
    }

    isChannelGroupSelected(group: ChannelGroup) {
        return (
            this.selectedPanel.type === DashboardPanelType.CHANNEL_GROUP &&
            this.selectedPanel.params.groupId === group.id
        );
    }

    isCustomGroupSelected(group: CustomGroup) {
        return (
            this.selectedPanel.type === DashboardPanelType.CUSTOM_GROUP &&
            this.selectedPanel.params.groupId === group.id
        );
    }

    isMimicSelected(mimic: any) {
        return (
            this.selectedPanel.type === DashboardPanelType.MIMICS &&
            this.selectedPanel.params.mimicId === mimic.id
        );
    }

    deleteSelectedPanel() {
        this.selectedPanel.type = null;
        this.selectedPanel.params = {};
    }

    onPreview() {
        this.onPreviewClick.emit();
    }

    isAnySelected() {
        return this.selectedPanel.type !== null;
    }
}
